'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import ReferralPanel from './ReferralPanel';
import GemDuelWrapper from './GemDuelWrapper';

interface PremiumGame {
  id: string;
  emoji: string;
  title: string;
  tagline: string;
  description: string;
  preview: string[];
  accentColor: string;
  glowColor: string;
  borderColor: string;
  difficulty: string;
  badge: string;
  playable?: boolean;
}

const PREMIUM_GAMES: PremiumGame[] = [
  {
    id: 'gem-duel',
    emoji: '💎',
    title: 'Gem Duel',
    tagline: 'Match Masters-inspired · Candivore, June 2017',
    description: 'Match 3+ gems to outscore an AI opponent across 10 turns. Earn sticker album pages to unlock devastating ability cards — row blasts, column crushes, color sweeps. Familiar core, ruthlessly sticky meta.',
    preview: ['🔴', '🔵', '🟢', '🟡', '🟣'],
    accentColor: '#a78bfa',
    glowColor: 'rgba(167,139,250,0.3)',
    borderColor: 'rgba(167,139,250,0.4)',
    difficulty: 'PvP DUEL',
    badge: 'MOST STICKY',
    playable: true,
  },
  {
    id: 'flow-state',
    emoji: '🌊',
    title: 'Flow State',
    tagline: 'Based on Csikszentmihalyi\'s Flow Theory (1990)',
    description: 'Difficulty auto-calibrates to your exact skill level in real time — keeping you in the razor-thin zone between boredom and anxiety where time disappears. Players average 11 minutes before they notice.',
    preview: ['🔵', '🟣', '⚪'],
    accentColor: '#5b8fff',
    glowColor: 'rgba(91,143,255,0.3)',
    borderColor: 'rgba(91,143,255,0.4)',
    difficulty: 'ADAPTIVE',
    badge: 'MOST IMMERSIVE',
  },
  {
    id: 'skinner-box',
    emoji: '🎰',
    title: 'The Skinner Box',
    tagline: 'Based on Skinner\'s Variable Reward Schedule (1938)',
    description: 'Unpredictable reward timing — not fixed, not random, but variable — triggers the same dopamine loop that makes slot machines impossible to put down. You never know when the next big hit lands.',
    preview: ['⭐', '💥', '🎯'],
    accentColor: '#ff9500',
    glowColor: 'rgba(255,149,0,0.3)',
    borderColor: 'rgba(255,149,0,0.4)',
    difficulty: 'BRUTAL',
    badge: 'MOST ADDICTIVE',
  },
  {
    id: 'cognitive-load',
    emoji: '🧩',
    title: 'Cognitive Overload',
    tagline: 'Based on Sweller\'s Cognitive Load Theory (1988)',
    description: 'Stacks multiple simultaneous tasks just beyond your working memory limit — 7±2 chunks. Each layer added forces your brain to chunk information faster, creating a compulsive "one more try" loop.',
    preview: ['🔴', '🟡', '🟢'],
    accentColor: '#ff3b6b',
    glowColor: 'rgba(255,59,107,0.3)',
    borderColor: 'rgba(255,59,107,0.4)',
    difficulty: 'HARD',
    badge: 'FAN FAVORITE',
  },
  {
    id: 'zeigarnik-loop',
    emoji: '🔁',
    title: 'The Unfinished Loop',
    tagline: 'Based on Bluma Zeigarnik\'s Interruption Effect (1927)',
    description: 'Every session ends exactly one step before completion — a half-built sequence, a near-perfect run, a chain one link short. Your brain cannot let go of unfinished tasks. You will come back. You always do.',
    preview: ['🔗', '⛓️', '❓'],
    accentColor: '#a78bfa',
    glowColor: 'rgba(167,139,250,0.3)',
    borderColor: 'rgba(167,139,250,0.4)',
    difficulty: 'PSYCHOLOGICAL',
    badge: 'HIGHEST RETURN RATE',
  },
  {
    id: 'ikea-effect',
    emoji: '🏗️',
    title: 'The Builder\'s Curse',
    tagline: 'Based on Norton, Mochon & Ariely\'s IKEA Effect (2012)',
    description: 'You construct a generative world — piece by piece, session by session. What you build with your own hands feels 5× more valuable than anything handed to you. Subscribers report it feels like their world, not ours.',
    preview: ['🧱', '🏠', '✨'],
    accentColor: '#34d399',
    glowColor: 'rgba(52,211,153,0.3)',
    borderColor: 'rgba(52,211,153,0.4)',
    difficulty: 'CREATIVE',
    badge: 'MOST OWNERSHIP',
  },
  {
    id: 'peak-end',
    emoji: '🎆',
    title: 'Peak & Fade',
    tagline: 'Based on Kahneman\'s Peak-End Rule (1993)',
    description: 'Engineered to deliver one jaw-dropping climactic moment per session — then end on a quiet, satisfying note. You won\'t remember the middle. You\'ll remember the peak. And you\'ll chase it again tomorrow.',
    preview: ['📈', '💥', '🌅'],
    accentColor: '#f59e0b',
    glowColor: 'rgba(245,158,11,0.3)',
    borderColor: 'rgba(245,158,11,0.4)',
    difficulty: 'CINEMATIC',
    badge: 'MOST MEMORABLE',
  },
  {
    id: 'mastery-curve',
    emoji: '📈',
    title: 'The Mastery Curve',
    tagline: 'Based on Deci & Ryan\'s Self-Determination Theory (1985)',
    description: 'A visible personal skill graph that grows with every session. You\'re not just playing — you\'re investing in yourself. Subscribers describe it as the only subscription that makes them feel smarter for paying.',
    preview: ['🧠', '📊', '🏅'],
    accentColor: '#06b6d4',
    glowColor: 'rgba(6,182,212,0.3)',
    borderColor: 'rgba(6,182,212,0.4)',
    difficulty: 'MASTERY',
    badge: 'BEST SELF-INVESTMENT',
  },
  {
    id: 'circle-packing',
    emoji: '⭕',
    title: 'Circle Packing',
    tagline: 'Generative Artistry — Algorithmic Beauty (2018)',
    description: 'Watch hundreds of circles grow in real time, each one expanding until it kisses a neighbor. The algorithm is mathematically elegant — it looks infinitely complex but runs on pure geometry. Tap to seed a new universe.',
    preview: ['🔵', '🟡', '🔴'],
    accentColor: '#e879f9',
    glowColor: 'rgba(232,121,249,0.3)',
    borderColor: 'rgba(232,121,249,0.4)',
    difficulty: 'ALGORITHMIC',
    badge: 'MOST SATISFYING',
  },
  {
    id: 'triangular-mesh',
    emoji: '🔺',
    title: 'Triangular Mesh',
    tagline: 'Generative Artistry — 3D Mesh Displacement (2018)',
    description: 'A grid of dots displaced by controlled randomness, stitched into triangles filled with 16 shades of gray. The same math that powers every 3D game engine — rendered as pure art. Each generation is a unique topographic map.',
    preview: ['🔺', '🔻', '⬛'],
    accentColor: '#94a3b8',
    glowColor: 'rgba(148,163,184,0.3)',
    borderColor: 'rgba(148,163,184,0.4)',
    difficulty: 'TECHNICAL',
    badge: 'MOST COMPLEX',
  },
  {
    id: 'hypnotic-squares',
    emoji: '🟦',
    title: 'Hypnotic Squares',
    tagline: 'Generative Artistry — William Kolomyjec, 1975',
    description: 'Recursion made visible. Each square spawns a smaller rotated square inside itself, spiraling inward until the eye loses track of where one ends and another begins. Based on Kolomyjec\'s 1975 computer art — still hypnotic 50 years later.',
    preview: ['🟦', '🟪', '⬜'],
    accentColor: '#818cf8',
    glowColor: 'rgba(129,140,248,0.3)',
    borderColor: 'rgba(129,140,248,0.4)',
    difficulty: 'RECURSIVE',
    badge: 'MOST HYPNOTIC',
  },
  {
    id: 'hours-of-dark',
    emoji: '🌑',
    title: 'Hours of Dark',
    tagline: 'Generative Artistry — Accept & Proceed, 2011',
    description: '365 strokes. One per day. Each stroke\'s width encodes the hours of darkness for that day of the year — thicker in winter, thinner in summer. A full year of planetary data rendered as a single meditative grid. Data art at its most poetic.',
    preview: ['🌑', '🌓', '🌕'],
    accentColor: '#fbbf24',
    glowColor: 'rgba(251,191,36,0.3)',
    borderColor: 'rgba(251,191,36,0.4)',
    difficulty: 'DATA ART',
    badge: 'MOST MEANINGFUL',
  },
];

const PERKS = [
  { icon: '🎮', text: '13 exclusive premium micro-games' },
  { icon: '🏆', text: 'Premium leaderboard — compete globally' },
  { icon: '⚔️', text: 'Head-to-head multiplayer challenges' },
  { icon: '🎨', text: 'Exclusive dark themes & animations' },
  { icon: '📊', text: 'Deep stats — reaction time, accuracy, trends' },
  { icon: '🚫', text: 'Zero ads, forever' },
];

export default function PremiumClient() {
  const router = useRouter();
  const [hoveredGame, setHoveredGame] = useState<string | null>(null);
  const [ctaPressed, setCtaPressed] = useState(false);
  const [activeGame, setActiveGame] = useState<string | null>(null);

  const handleSubscribe = () => {
    setCtaPressed(true);
    // TODO: Connect Supabase auth check + Stripe payment flow
    // if (!user) router.push('/login?redirect=/premium')
    // else initStripeCheckout({ priceId: 'price_xxx', successUrl: '/premium/success' })
    setTimeout(() => setCtaPressed(false), 300);
  };

  const handlePlay = (gameId: string) => {
    setActiveGame(gameId);
  };

  const handleClose = () => {
    setActiveGame(null);
  };

  return (
    <div className="relative z-10 flex flex-col min-h-screen overflow-hidden">
      {/* Animated background */}
      <div className="fixed inset-0 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 80% 60% at 50% 20%, rgba(255,210,63,0.07) 0%, rgba(123,91,255,0.05) 40%, rgba(10,10,15,0) 70%)',
          }}
        />
        {/* Floating orbs */}
        <div
          className="absolute rounded-full opacity-10 pulse-anim"
          style={{
            width: 400,
            height: 400,
            top: '-100px',
            right: '-100px',
            background: 'radial-gradient(circle, #ffd23f 0%, transparent 70%)',
          }}
        />
        <div
          className="absolute rounded-full opacity-8 pulse-anim"
          style={{
            width: 300,
            height: 300,
            bottom: '10%',
            left: '-80px',
            background: 'radial-gradient(circle, #7b5bff 0%, transparent 70%)',
            animationDelay: '1.5s',
          }}
        />
      </div>

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-5 pt-5 pb-3">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => router.push('/')}>
          <AppLogo size={28} />
          <span className="font-bold text-base tracking-tight text-foreground">Unbored</span>
        </div>
        <div
          className="px-3 py-1 rounded-full text-xs font-black tracking-widest uppercase"
          style={{
            background: 'linear-gradient(135deg, rgba(255,210,63,0.2), rgba(255,210,63,0.08))',
            border: '1px solid rgba(255,210,63,0.5)',
            color: '#ffd23f',
          }}
        >
          ✦ PREMIUM
        </div>
      </header>

      <div className="relative z-10 flex flex-col items-center px-5 py-6 gap-8 max-w-lg mx-auto w-full">

        {/* Hero headline */}
        <div className="text-center">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase mb-4"
            style={{
              background: 'rgba(255,210,63,0.1)',
              border: '1px solid rgba(255,210,63,0.3)',
              color: '#ffd23f',
            }}
          >
            <span>✦</span>
            <span>Exclusive Access</span>
            <span>✦</span>
          </div>
          <h1
            className="font-black leading-tight text-foreground"
            style={{ fontSize: '2.2rem', letterSpacing: '-0.03em' }}
          >
            Games too good
            <br />
            <span
              style={{
                background: 'linear-gradient(135deg, #ffd23f 0%, #ff9500 50%, #ff3b6b 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              for the free tier.
            </span>
          </h1>
          <p className="mt-3 text-muted-foreground text-sm font-medium max-w-xs mx-auto leading-relaxed">
            Thirteen micro-games engineered to rewire your brain. One match-3 duel with ability cards. Seven grounded in real psychology studies. Five built from the most beautiful generative art algorithms ever written. Locked behind a paywall for a reason.
          </p>
        </div>

        {/* Inline game modal */}
        {activeGame === 'gem-duel' && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
          >
            <div
              className="relative w-full max-w-md rounded-2xl overflow-hidden"
              style={{
                background: 'linear-gradient(145deg, #16162a 0%, #0f0f1e 100%)',
                border: '1px solid rgba(167,139,250,0.4)',
                boxShadow: '0 0 60px rgba(167,139,250,0.2), 0 24px 64px rgba(0,0,0,0.6)',
                maxHeight: '92vh',
                overflowY: 'auto',
              }}
            >
              {/* Modal header */}
              <div
                className="flex items-center justify-between px-5 py-4"
                style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl">💎</span>
                  <div>
                    <h3 className="font-black text-foreground text-sm leading-tight">Gem Duel</h3>
                    <p className="text-xs font-semibold" style={{ color: '#a78bfa' }}>Match Masters Style · PvP Duel</p>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-black"
                  style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)', border: 'none', cursor: 'pointer' }}
                >
                  ✕
                </button>
              </div>
              <GemDuelWrapper onClose={handleClose} />
            </div>
          </div>
        )}

        {/* Locked game cards */}
        <div className="w-full flex flex-col gap-4">
          {PREMIUM_GAMES.map((game, idx) => {
            const isHovered = hoveredGame === game.id;
            return (
              <div
                key={game.id}
                className="relative rounded-2xl overflow-hidden cursor-pointer"
                style={{
                  background: isHovered
                    ? `linear-gradient(135deg, rgba(${game.accentColor === '#ff3b6b' ? '255,59,107' : game.accentColor === '#7b5bff' ? '123,91,255' : '54,226,164'},0.12), rgba(18,18,31,0.95))`
                    : 'linear-gradient(135deg, rgba(22,22,42,0.9), rgba(15,15,30,0.95))',
                  border: `1px solid ${isHovered ? game.borderColor : 'rgba(42,42,69,0.8)'}`,
                  boxShadow: isHovered ? `0 0 30px ${game.glowColor}, 0 8px 32px rgba(0,0,0,0.4)` : '0 4px 16px rgba(0,0,0,0.3)',
                  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  transform: isHovered ? 'translateY(-2px) scale(1.01)' : 'translateY(0) scale(1)',
                }}
                onMouseEnter={() => setHoveredGame(game.id)}
                onMouseLeave={() => setHoveredGame(null)}
                onTouchStart={() => setHoveredGame(game.id)}
                onTouchEnd={() => setHoveredGame(null)}
              >
                {/* Badge */}
                <div
                  className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-xs font-black tracking-wider"
                  style={{
                    background: `${game.glowColor}`,
                    border: `1px solid ${game.borderColor}`,
                    color: game.accentColor,
                    fontSize: '0.6rem',
                  }}
                >
                  {game.badge}
                </div>

                <div className="p-5">
                  <div className="flex items-start gap-4">
                    {/* Emoji + lock overlay */}
                    <div className="relative flex-shrink-0">
                      <div
                        className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl"
                        style={{
                          background: `linear-gradient(135deg, ${game.glowColor}, rgba(18,18,31,0.8))`,
                          border: `1px solid ${game.borderColor}`,
                          filter: 'blur(0px)',
                        }}
                      >
                        {game.emoji}
                      </div>
                      {/* Lock icon or play indicator */}
                      <div
                        className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-xs"
                        style={{
                          background: '#0a0a0f',
                          border: game.playable ? '2px solid rgba(167,139,250,0.8)' : '2px solid rgba(255,210,63,0.6)',
                          color: game.playable ? '#a78bfa' : '#ffd23f',
                        }}
                      >
                        {game.playable ? '▶' : '🔒'}
                      </div>
                    </div>

                    {/* Game info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h3 className="font-black text-foreground text-base">{game.title}</h3>
                        <span
                          className="text-xs font-bold px-1.5 py-0.5 rounded"
                          style={{
                            background: 'rgba(255,255,255,0.06)',
                            color: 'rgba(255,255,255,0.4)',
                            fontSize: '0.6rem',
                            letterSpacing: '0.1em',
                          }}
                        >
                          {game.difficulty}
                        </span>
                      </div>
                      <p className="text-xs font-semibold mb-1.5" style={{ color: game.accentColor }}>
                        {game.tagline}
                      </p>
                      <p className="text-xs text-muted-foreground leading-relaxed">{game.description}</p>
                    </div>
                  </div>

                  {/* Preview emojis — blurred teaser, or play button */}
                  <div className="mt-4 flex items-center gap-2">
                    {game.playable ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); handlePlay(game.id); }}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl font-black text-xs"
                        style={{
                          background: 'linear-gradient(135deg, rgba(167,139,250,0.2), rgba(167,139,250,0.08))',
                          border: '1px solid rgba(167,139,250,0.5)',
                          color: '#a78bfa',
                          cursor: 'pointer',
                          letterSpacing: '0.05em',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        ▶ Play Now
                      </button>
                    ) : (
                      <div className="flex gap-1.5">
                        {game.preview.map((p, i) => (
                          <span
                            key={i}
                            className="text-xl"
                            style={{
                              filter: 'blur(3px)',
                              opacity: 0.5,
                              transition: 'all 0.3s ease',
                              ...(isHovered ? { filter: 'blur(1.5px)', opacity: 0.7 } : {}),
                            }}
                          >
                            {p}
                          </span>
                        ))}
                      </div>
                    )}
                    {!game.playable && (
                      <span
                        className="text-xs font-semibold ml-2"
                        style={{ color: 'rgba(255,255,255,0.25)' }}
                      >
                        preview locked
                      </span>
                    )}
                  </div>
                </div>

                {/* Frosted lock overlay — only for non-playable games */}
                {!game.playable && (
                  <div
                    className="absolute inset-0 flex items-center justify-center rounded-2xl"
                    style={{
                      background: 'rgba(10,10,15,0.45)',
                      backdropFilter: 'blur(1px)',
                      opacity: isHovered ? 0 : 0.6,
                      transition: 'opacity 0.3s ease',
                      pointerEvents: 'none',
                    }}
                  />
                )}

                {/* Sequence number */}
                <div
                  className="absolute bottom-3 left-5 font-black font-mono-nums"
                  style={{
                    fontSize: '4rem',
                    color: 'rgba(255,255,255,0.03)',
                    lineHeight: 1,
                    userSelect: 'none',
                  }}
                >
                  0{idx + 1}
                </div>
              </div>
            );
          })}
        </div>

        {/* Pricing CTA card */}
        <div
          className="w-full rounded-2xl overflow-hidden"
          style={{
            background: 'linear-gradient(145deg, #16162a 0%, #0f0f1e 100%)',
            border: '1px solid rgba(255,210,63,0.3)',
            boxShadow: '0 0 40px rgba(255,210,63,0.08), 0 16px 48px rgba(0,0,0,0.4)',
          }}
        >
          {/* Gold top bar */}
          <div
            className="h-1 w-full"
            style={{
              background: 'linear-gradient(90deg, #ffd23f 0%, #ff9500 50%, #ff3b6b 100%)',
            }}
          />

          <div className="p-6 flex flex-col gap-5">
            {/* Price */}
            <div className="flex items-end gap-2">
              <span
                className="font-black leading-none"
                style={{
                  fontSize: '3.5rem',
                  background: 'linear-gradient(135deg, #ffd23f 0%, #ff9500 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                $4.99
              </span>
              <span className="text-muted-foreground text-sm font-semibold mb-2">/ month</span>
              <span
                className="ml-auto text-xs font-bold px-2 py-1 rounded-full mb-2"
                style={{
                  background: 'rgba(54,226,164,0.12)',
                  border: '1px solid rgba(54,226,164,0.3)',
                  color: '#36e2a4',
                }}
              >
                Cancel anytime
              </span>
            </div>

            {/* Perks list */}
            <div className="grid grid-cols-1 gap-2">
              {PERKS.map((perk) => (
                <div key={perk.text} className="flex items-center gap-3">
                  <span className="text-base flex-shrink-0">{perk.icon}</span>
                  <span className="text-sm text-foreground font-medium">{perk.text}</span>
                </div>
              ))}
            </div>

            {/* CTA button */}
            <button
              onClick={handleSubscribe}
              className="w-full py-4 rounded-xl font-black text-base tracking-wide relative overflow-hidden"
              style={{
                background: ctaPressed
                  ? 'linear-gradient(135deg, #c4820a 0%, #c4205a 100%)'
                  : 'linear-gradient(135deg, #ffd23f 0%, #ff9500 50%, #ff3b6b 100%)',
                color: ctaPressed ? 'rgba(255,255,255,0.9)' : '#0a0a0f',
                boxShadow: ctaPressed
                  ? '0 4px 20px rgba(255,210,63,0.2)'
                  : '0 0 40px rgba(255,210,63,0.35), 0 8px 32px rgba(255,149,0,0.25)',
                transform: ctaPressed ? 'scale(0.97) translateY(2px)' : 'scale(1)',
                transition: 'all 150ms ease',
                border: 'none',
                letterSpacing: '0.05em',
              }}
            >
              <span className="relative z-10">✦ Unlock Premium — $4.99/mo</span>
            </button>

            {/* Auth note */}
            <p className="text-center text-xs text-muted-foreground leading-relaxed">
              Secure checkout via Stripe.{' '}
              <span className="text-foreground font-semibold">Sign in required.</span>
              {' '}Your streak data carries over.
            </p>
          </div>
        </div>

        {/* Social proof strip */}
        <div className="w-full flex items-center justify-center gap-6 py-2">
          {[
            { value: '13', label: 'exclusive games' },
            { value: '∞', label: 'replays' },
            { value: '0', label: 'ads ever' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div
                className="font-black font-mono-nums text-xl"
                style={{
                  background: 'linear-gradient(135deg, #ffd23f 0%, #ff9500 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {stat.value}
              </div>
              <div className="text-xs text-muted-foreground font-medium">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Referral / Invite section */}
        <div className="w-full">
          <div className="flex items-center gap-2 mb-4">
            <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.06)' }} />
            <span
              className="text-xs font-black tracking-widest uppercase px-3"
              style={{ color: 'rgba(255,255,255,0.25)' }}
            >
              Invite & Earn
            </span>
            <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.06)' }} />
          </div>
          <ReferralPanel />

          {/* Leaderboard link */}
          <button
            onClick={() => router.push('/referral-leaderboard')}
            className="w-full mt-3 flex items-center justify-between px-4 py-3 rounded-2xl transition-all"
            style={{
              background: 'rgba(54,226,164,0.06)',
              border: '1px solid rgba(54,226,164,0.2)',
            }}
          >
            <div className="flex items-center gap-2.5">
              <span className="text-lg">🏆</span>
              <div className="text-left">
                <p className="text-xs font-black text-foreground leading-tight">Referral Leaderboard</p>
                <p className="text-xs text-muted-foreground font-medium">See who's spreading the word</p>
              </div>
            </div>
            <span className="text-xs font-black" style={{ color: '#36e2a4' }}>View →</span>
          </button>

          {/* Cosmetics link */}
          <button
            onClick={() => router.push('/cosmetics')}
            className="w-full mt-2 flex items-center justify-between px-4 py-3 rounded-2xl transition-all"
            style={{
              background: 'rgba(167,139,250,0.06)',
              border: '1px solid rgba(167,139,250,0.2)',
            }}
          >
            <div className="flex items-center gap-2.5">
              <span className="text-lg">✨</span>
              <div className="text-left">
                <p className="text-xs font-black text-foreground leading-tight">My Cosmetics</p>
                <p className="text-xs text-muted-foreground font-medium">Equip badges to your profile avatar</p>
              </div>
            </div>
            <span className="text-xs font-black" style={{ color: '#a78bfa' }}>View →</span>
          </button>
        </div>

        {/* Back link */}
        <button
          onClick={() => router.push('/')}
          className="btn-ghost px-6 py-2.5 text-sm mb-4"
        >
          ← Back to free games
        </button>
      </div>

      {activeGame && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
          <div className="relative z-10 w-full max-w-md bg-white border border-gray-200 rounded-2xl shadow-xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-lg font-bold text-foreground">{PREMIUM_GAMES.find((g) => g.id === activeGame)?.title}</span>
              <button className="btn-ghost" onClick={handleClose}>
                <svg className="w-4 h-4" aria-hidden="true" viewBox="0 0 20 20">
                  <path d="M10 18a8 8 0 100-16 8 8 8 0 000 16zM6 10a4 4 0 118 0 4 4 0 01-8 0zM14 10a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </button>
            </div>
            <div className="text-sm text-muted-foreground mb-4">
              {PREMIUM_GAMES.find((g) => g.id === activeGame)?.description}
            </div>
            <div className="flex flex-wrap gap-2">
              {PREMIUM_GAMES.find((g) => g.id === activeGame)?.preview.map((p, i) => (
                <span key={i} className="text-xl">
                  {p}
                </span>
              ))}
            </div>
            <div className="mt-4">
              <button
                className="w-full py-3 rounded-xl font-black text-base tracking-wide bg-foreground text-white"
                onClick={() => handlePlay(activeGame)}
              >
                Play Game
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
